import React from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, Typography } from '@mui/material';
import { useClinicTimezone } from '../../hooks/useClinicTimezone';
import { formatAnchoredDayOrInstant, formatCalendarDate } from '../../utils/dateUtils';

interface ExistingVaccinationDialogProps {
  open: boolean;
  vaccineName: string;
  horseName: string;
  /** The vaccinations of this vaccine already on the chart for the note's day. */
  doses: any[];
  /** "It is that dose": nothing is recorded, and the plan line is not a medication either. */
  onSame: () => void;
  /** "A new dose": it is offered as a vaccination record like any other. */
  onNew: () => void;
  /** Back: the note stays open, nothing decided. */
  onBack: () => void;
}

const DAY = 'MMM d, yyyy';

/**
 * Asks before a SOAP note records a vaccine that is already on the chart for that day (spec §2, the
 * practice manager's link-or-create prompt). A match is only a suggestion; the vet says which it is.
 */
const ExistingVaccinationDialog: React.FC<ExistingVaccinationDialogProps> = ({
  open,
  vaccineName,
  horseName,
  doses,
  onSame,
  onNew,
  onBack,
}) => {
  const tz = useClinicTimezone();
  return (
    <Dialog open={open} onClose={onBack} maxWidth="sm" fullWidth>
      <DialogTitle>Existing vaccination found</DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {vaccineName} is already on {horseName || 'this patient'}&rsquo;s chart for this day.
        </Typography>
        <Stack spacing={1} sx={{ mb: 2.5 }}>
          {doses.map((d) => (
            <Paper key={d.id} variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }}>
              <Typography variant="body2" fontWeight={600}>
                {d.vaccineName} · {formatAnchoredDayOrInstant(d.dateAdministered, tz, DAY)}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {[
                  d.administeredBy && `Given by ${d.administeredBy}`,
                  d.lotNumber ? `Lot ${d.lotNumber}${d.lotExpiresOn ? `, exp ${formatCalendarDate(d.lotExpiresOn, DAY)}` : ''}` : 'No lot recorded',
                  d.doseMl && `${d.doseMl} mL`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
            </Paper>
          ))}
        </Stack>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
          Is this note about that dose?
        </Typography>
        <Stack spacing={1.5}>
          <Box>
            <Button fullWidth variant="contained" onClick={onSame}>
              Yes, it&rsquo;s that dose
            </Button>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Nothing new is recorded and no more stock is used.
            </Typography>
          </Box>
          <Box>
            <Button fullWidth variant="outlined" onClick={onNew}>
              No, record a new dose
            </Button>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              A second vaccination is recorded and its dose comes out of stock.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onBack}>Back</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExistingVaccinationDialog;
